import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useBuyerLists } from "@/hooks/useBuyerLists";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useIsMobile } from "@/hooks/use-mobile";
import { List, Plus, Users, Calendar, Edit, Trash2, Eye } from "lucide-react";
import { format } from "date-fns";
import SellerRestrictedPage from "@/components/seller/SellerRestrictedPage";
import { CreateManualBuyerDialog } from "@/components/seller/dialogs/CreateManualBuyerDialog";

const BuyerListsPage = () => {
  usePageTitle("My Buyer Lists");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { lists, isLoading, createList, deleteList, fetchLists } = useBuyerLists();
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCreateBuyerDialog, setShowCreateBuyerDialog] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    
    setIsCreating(true);
    const result = await createList(newListName.trim(), newListDescription.trim() || undefined);
    
    if (result.success) {
      setShowCreateDialog(false);
      setNewListName("");
      setNewListDescription("");
    }
    setIsCreating(false);
  };

  const handleDeleteList = async (listId: string) => {
    if (window.confirm("Are you sure you want to delete this list? This action cannot be undone.")) {
      await deleteList(listId);
    }
  };

  const handleViewList = (listId: string) => {
    navigate(`/seller/buyer-lists/${listId}`);
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading your buyer lists...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <SellerRestrictedPage featureName="buyer lists">
      <div className="py-6 space-y-6">
      {/* Page Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <List className="h-6 w-6" />
                My Buyer Lists
              </CardTitle>
              <p className="text-muted-foreground mt-1">
                Manage your saved buyer contacts and create targeted lists
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                onClick={() => navigate('/seller/search-buyers')}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Users className="h-4 w-4 mr-2" />
                {isMobile ? "Search" : "Search Buyers"}
              </Button>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full sm:w-auto" data-tour="create-list">
                    <Plus className="h-4 w-4 mr-2" />
                    Create List
                  </Button>
                </DialogTrigger>
                <Button 
                  variant="outline"
                  onClick={() => setShowCreateBuyerDialog(true)}
                  className="w-full sm:w-auto border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700] hover:text-[#080c14]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {isMobile ? "Add Private Buyer" : "Add Private Buyer"}
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Buyer List</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="listName">List Name *</Label>
                      <Input
                        id="listName"
                        placeholder="e.g., Cash Buyers - Florida"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="listDescription">Description</Label>
                      <Textarea
                        id="listDescription"
                        placeholder="Optional description for this list..."
                        value={newListDescription}
                        onChange={(e) => setNewListDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={handleCreateList}
                        disabled={!newListName.trim() || isCreating}
                        className="flex-1"
                      >
                        {isCreating ? "Creating..." : "Create List"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowCreateDialog(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Lists Overview */}
      {lists.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <List className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No buyer lists yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first buyer list to start organizing your contacts.
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First List
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tour="list-cards">
          {lists.map((list, index) => (
            <Card key={list.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{list.list_name}</CardTitle>
                    {list.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {list.description}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1" {...(index === 0 ? { 'data-tour': 'buyer-count' } : {})}>
                    <Users className="h-4 w-4" />
                    <span>{list.buyer_count || 0} buyers</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(list.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2" {...(index === 0 ? { 'data-tour': 'list-actions' } : {})}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewList(list.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {isMobile ? "View" : "View"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/seller/buyer-lists/${list.id}/edit`)}
                    className="px-2"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteList(list.id)}
                    className="px-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <CreateManualBuyerDialog
        open={showCreateBuyerDialog}
        onOpenChange={setShowCreateBuyerDialog}
        onBuyerCreated={() => {
          fetchLists(); // Refresh lists to show updated buyer counts or new lists
        }}
      />
      </div>
    </SellerRestrictedPage>
  );
};

export default BuyerListsPage;